import { NameForms, PhoneForm, AddressForms } from "@/components/fragments/forms";
import { Flex } from "@chakra-ui/react";
import { Box } from "@/components/essentials/box";
import { Typography } from "@/components/essentials/typography";
import { useState, type FC } from "react";
import { EmailForm } from "@/components/fragments/forms/email-form";
import {
  shipmentFormAddressField,
  shipmentFormBuildingNameAndRoomNumberField,
  shipmentFormCityField,
  shipmentFormDescriptionTestId,
  shipmentFormFirstNameField,
  shipmentFormLastNameField,
  shipmentFormMailField,
  shipmentFormPhoneNumberField,
  shipmentFormPrefectureField,
  shipmentFormTestId,
  shipmentFormTitleTestId,
  shipmentFormZipCodeField,
} from "./const";
import { useFormContext, useWatch } from "react-hook-form";
import { type ShipmentFormValue } from "./type";
import { SelectContractorSameOrNewForm } from "./__components__/SelectContractorSameOrNewForm";
import { Button } from "@/components/essentials/button";
import { ChatInputBox } from "@/components/fragments/box/chat-input-box";

interface Props {
  readonly isStepwise?: boolean;
  readonly showChatBalloon?: boolean;
  readonly onCompleteAllSteps?: () => void;
}

export const ShipmentForm: FC<Props> = ({ isStepwise = false, showChatBalloon = false, onCompleteAllSteps }) => {
  const {
    control,
    formState: { isValid },
  } = useFormContext<ShipmentFormValue>();
  const [formStepIndex, setFormStepIndex] = useState<number>(0);

  const contractorSameOrNew = useWatch<ShipmentFormValue, "shippingAddressSameOrNew">({
    control,
    name: "shippingAddressSameOrNew",
  });
  const requiredAddress = contractorSameOrNew === "new";

  return (
    <Flex flexDir="column" gridGap="10" data-testid={shipmentFormTestId}>
      {!showChatBalloon && (
        <Box wordBreak="break-word" textAlign="center">
          <Typography variant="display3" data-testid={shipmentFormTitleTestId}>
            お届け先情報の入力
          </Typography>
          <Typography variant="text4" color="gray.dark" data-testid={shipmentFormDescriptionTestId}>
            お届け先住所を選択してください。別のお届け先を指定する場合はお届け先情報を入力してください。
          </Typography>
        </Box>
      )}
      <Flex flexDir="column" gridGap="1.5rem">
        <ChatInputBox showChatBalloon={showChatBalloon} title="お届け先について教えてください">
          <SelectContractorSameOrNewForm />
        </ChatInputBox>

        <ChatInputBox showChatBalloon={requiredAddress && showChatBalloon} title="お届け先の住所について教えてください">
          <NameForms
            hidden={!requiredAddress}
            required={requiredAddress}
            firstNameField={shipmentFormFirstNameField}
            lastNameField={shipmentFormLastNameField}
          />
          <Box hidden={!requiredAddress} my={showChatBalloon ? "1.5rem" : 0}>
            <PhoneForm required={requiredAddress} phoneNumberField={shipmentFormPhoneNumberField} />
          </Box>
          <AddressForms
            hidden={!requiredAddress}
            required={requiredAddress}
            zipCodeField={shipmentFormZipCodeField}
            prefectureField={shipmentFormPrefectureField}
            cityField={shipmentFormCityField}
            addressField={shipmentFormAddressField}
            buildingNameAndRoomNumberField={shipmentFormBuildingNameAndRoomNumberField}
            prefectureMode="code"
          />
          <Box hidden={!requiredAddress} mt={showChatBalloon ? "1.5rem" : 0}>
            <EmailForm required={requiredAddress} emailField={shipmentFormMailField} />
          </Box>
        </ChatInputBox>
      </Flex>
      {isStepwise && formStepIndex <= 0 && (
        <Button
          type="button"
          size="md"
          m="auto"
          onClick={() => {
            if (onCompleteAllSteps) {
              onCompleteAllSteps();
            }

            setFormStepIndex((current: number) => current + 1);
          }}
          isDisabled={!isValid}
        >
          次へ
        </Button>
      )}
    </Flex>
  );
};
